import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import omit from 'lodash/omit';
import * as userSelectors from 'selectors/user';
import { FormattedMessage, useIntl } from 'react-intl';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import ConfirmPasswordDialog from 'modules/users/components/confirm-password-dialog';
import BankIdLoginDialog from 'modules/users/components/bankid-login-dialog';
import * as actions from 'actions';
import Api from 'api';
import { cleanHostInformationRequestData } from 'logic/userForm';
import HostInformationForm from 'modules/users/components/HostInformationForm';
import DialogTemplate from 'components/shared/DialogTemplate';

const HostInformationDialog = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useSelector(userSelectors.userData);
  const mangoUserId = useSelector(userSelectors.mangoUserId);
  const [loading, setLoading] = React.useState(false);
  const [openPassword, setOpenPassword] = React.useState(false);
  const [openBankid, setOpenBankid] = React.useState(false);
  const [submittedData, setSubmittedData] = React.useState(null);
  const [hostDialog, openHostDialog] = React.useState(true);

  const fullscreen = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  React.useEffect(() => {
    setOpenBankid(false);
  }, []);

  const onSubmit = async ({ password, ...values }) => {
    const errorMsg = intl.formatMessage({ id: 'form.error', defaultMessage: 'Error in form!' });
    const preparedData = {
      ...omit(cleanHostInformationRequestData(values), ['iban', 'owner']),
      password,
    };
    setLoading(true);

    try {
      const res = await Api.updateUserHostInformation(preparedData);

      dispatch(actions.fetchUserSelfSuccess(res));
      dispatch(
        actions.showSnackbar({
          severity: 'success',
          message: {
            id: 'success.informationSaved',
            defaultMessage: 'Your information has been saved correctly',
          },
        }),
      );
    } catch (err) {
      dispatch(
        actions.showSnackbar({
          severity: 'error',
          message: {
            id: err.data.message,
            defaultMessage: errorMsg,
          },
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const onPreSubmit = (values) => {
    if (user.type === 'STRICT' && !user.bankid_verified_at) {
      setSubmittedData(values);

      return setOpenPassword(true);
    }

    if (user.type === 'STRICT' && user.bankid_verified_at && !user.bankid_session) {
      setSubmittedData(values);

      return setOpenBankid(true);
    }

    return onSubmit(values);
  };

  return (
    <Dialog
      fullScreen={fullscreen}
      fullWidth
      maxWidth="lg"
      onClose={() => openHostDialog(false)}
      open={hostDialog}
    >
      <DialogTemplate
        title={
          <FormattedMessage
            id="booking.create.dac7"
            defaultMessage="Before creating a booking please complete the information below"
          />
        }
        isUpdating={loading}
        onClose={() => openHostDialog(false)}
      >
        <Box px={2}>
          <HostInformationForm
            mangoUserId={mangoUserId}
            onSubmit={onPreSubmit}
            user={user}
            bank={{}}
            loading={loading}
            requiredIban={false}
          />
        </Box>
        <BankIdLoginDialog
          open={openBankid}
          header={<FormattedMessage id="bankid.login" defaultMessage="Login with BankID" />}
          title={
            <Box textAlign="center">
              <FormattedMessage
                id="bankid.security"
                defaultMessage="For security reasons, please log in with your BankID before continuing"
              />
            </Box>
          }
          onSuccess={() => {
            setOpenBankid(false);
            onSubmit(submittedData);
          }}
          onClose={() => setOpenBankid(false)}
          footer={
            !user.bankid_verified_at && (
              <>
                <Box textAlign="center">
                  <>
                    <FormattedMessage
                      id="If you do not have BankID"
                      defaultMessage="If you do not have BankID"
                    />
                    &nbsp;
                    <Box
                      component="span"
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={() => setOpenBankid(false)}
                    >
                      <FormattedMessage id="click here" defaultMessage="click here" />
                    </Box>
                  </>
                </Box>
              </>
            )
          }
        />
        <ConfirmPasswordDialog
          onClose={() => setOpenPassword(false)}
          open={openPassword}
          onSuccess={(password) => {
            setOpenPassword(false);

            return onSubmit({
              ...submittedData,
              password,
            });
          }}
        />
      </DialogTemplate>
    </Dialog>
  );
};

export default HostInformationDialog;
